import React, { FC, useCallback, useEffect, useState } from 'react'
import { Box, Paper } from '@mui/material'
import {
  DataGridPremium,
  useGridApiRef,
  GridPaginationModel,
  gridFilteredTopLevelRowCountSelector,
  gridFilterModelSelector,
  GridColDef,
  GridValidRowModel,
  GridPinnedColumns
} from '@mui/x-data-grid-premium'
import { Gateway } from 'src/api'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'
import { TableToolbar } from 'src/components/ui'

const defaultRowsPerPageOptions = defaultRowsValuePerPage
const defaultPerPage = 25
const defaultPage = 1
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`
}

interface CommonGatewayListProps<Entity extends GridValidRowModel = any> {
  columns: GridColDef<Entity>[]
  data: Entity[]
  isLoading: boolean
  count: number
  autoHeight?: boolean
}

export const CommonGatewayList: FC<CommonGatewayListProps> = ({ columns, data, isLoading, count, autoHeight = false }) => {
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const apiRef = useGridApiRef()
  const [rowCountState, setRowCountState] = useState(count)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>({
    right: ['actions']
  })

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState))
  }, [count, setRowCountState])

  const handlePinnedColumnsChange = useCallback((updatedPinnedColumns: GridPinnedColumns) => {
    setPinnedColumns(updatedPinnedColumns)
  }, [])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const newPage = pagination.page + 1
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${newPage}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    } else setRowCountState(count)
  }

  const page = queryParams['page'] ? parseInt(queryParams['page'], 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams['rowsPerPage'], 10) || defaultPerPage

  return (
    <Box height="100%">
      <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%', maxHeight: '800px' }}>
        <DataGridPremium<Gateway>
          apiRef={apiRef}
          pagination
          autoHeight={autoHeight}
          pinnedColumns={pinnedColumns}
          rows={data}
          columns={columns}
          rowCount={rowCountState}
          loading={isLoading}
          paginationModel={{ page, pageSize }}
          rowThreshold={2}
          columnThreshold={2}
          paginationMode="server"
          pageSizeOptions={defaultRowsPerPageOptions}
          onPaginationModelChange={handleChangePaginationModel}
          onStateChange={handleChangeState}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          localeText={{
            columnMenuSortAsc: 'Sort A-Z',
            columnMenuSortDesc: 'Sort Z-A'
          }}
          slots={{
            toolbar: () => <TableToolbar />
          }}
          sx={(theme) => ({
            border: 'none',
            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
              paddingLeft: '5px',
              overflow: 'visible'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.6)',
              overflow: 'visible'
            }
          })}
        />
      </Paper>
    </Box>
  )
}
